<template>
    <div class="job">
        <section class="light-gray-bg">
            <div class="px-4 lg:px-12 md:container md:mx-auto py-12">
                <nuxt-link to="/jobs"><button class="btn btn-back">Back to Jobs</button></nuxt-link>
                <h1 class="page-title text-primary mt-4">{{ job.Title }}</h1>
                <p class="mt-6 text-xl font-medium uppercase">{{ `${job.Company} - ${job.Location}` }}</p>
                <p class="mt-2 text-xl"><span class="font-medium">Job ID: </span><span>{{ job.JobID }}</span></p>
                <p class="mt-2 text-xl"><span class="font-medium">Posted On: </span><span>{{ dayjs(job.PublishDate).format('MMM D, YYYY') }}</span></p>
                <div class="mt-8">
                    <nuxt-link to="#applyJob">
                        <button class="btn btn-secondary">Apply Now</button>
                    </nuxt-link>
                    
                </div>
            </div>
        </section>
        <section class="content">
            <div class="px-4 lg:px-12 md:container md:mx-auto py-12 lg:py-32">
                <ContentDoc :path="$route.path"></ContentDoc>
            </div>
        </section>
        <section id="applyJob">
            <div class="px-4 lg:px-12 md:container md:mx-auto pb-12 lg:pb-32">
                <div class="lg:px-48">
                    <h3 class="text-primary text-center text-3xl font-semibold">Apply for this job</h3>
                    <form class="register-form light-gray-bg p-4 lg:p-8 mt-16" @submit.prevent="submitForm">
                            <div class="grid lg:grid-cols-2 gap-4">
                                <div>
                                    <label for="first-name">First Name<sup>*</sup></label>
                                    <input type="text" id="first-name" v-model="JobApplicationData.FirstName" required>
                                </div>
                                <div>
                                    <label for="last-name">Last Name<sup>*</sup></label>
                                    <input type="text" id="last-name" v-model="JobApplicationData.LastName" required>
                                </div>
                            </div>
                            <div class="grid lg:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label for="email-id">Email Address<sup>*</sup></label>
                                    <input type="email" id="email-id" v-model="JobApplicationData.Email" required>
                                </div>
                                <div>
                                    <label for="contact-no">Contact Number<sup>*</sup></label>
                                    <input type="tel" id="contact-no" v-model="JobApplicationData.Phone" required>
                                </div>
                            </div>
                            <div class="grid lg:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label for="location">Location</label>
                                    <input type="text" id="location" v-model="JobApplicationData.Location">
                                </div>
                                <div>
                                    <label for="dob">Date of Birth</label>
                                    <Datepicker v-model="JobApplicationData.DOB" placeholder="DD/MM/YYYY" text-input auto-apply :close-on-auto-apply="true" :enable-time-picker="false" :max-date="new Date()" format="dd/MM/yyyy" />
                                </div>
                            </div>
                            <div class="mt-4">
                                <label for="experience-years">How many years of childcare experience do you have?</label>
                                <input type="text" id="experience-years" v-model="JobApplicationData.ExperienceYear">
                            </div>
                            <div class="mt-4">
                                <label for="experience">Brief description of your experience in child care.</label>
                                <textarea id="experience" rows="4" v-model="JobApplicationData.ExperienceDetails"></textarea>
                            </div>
                            <div class="mt-4">
                                <label for="Qualifications">Qualifications</label>
                                <input type="text" id="Qualifications" v-model="JobApplicationData.Qualifications">
                            </div>
                            <div class="grid lg:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label for="job_type">What sort of work are you looking for?</label>
                                    <select id="job_type" v-model="JobApplicationData.WorkType">
                                        <option disabled value="">Please select ...</option>
                                        <option value="Full Time">Full Time</option>
                                        <option value="Part Time">Part Time</option>
                                        <option value="Term Time">Term Time</option>
                                    </select>
                                </div>
                                <div>
                                    <label for="dbs">Do you have a current DBS?</label>
                                    <select id="dbs" v-model="JobApplicationData.DBS">
                                        <option disabled value="">Please select ...</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>
                            <div class="mt-8">
                                <button class="btn btn-secondary btn-full" :disabled="isFormValid">
                                    <span v-if="!isLoading">Submit Form</span>
                                    <span v-if="isLoading">Sending...</span>
                                </button>
                            </div>
                        </form>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import dayjs from 'dayjs';
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { ref, reactive } from 'vue';
import Swal from 'sweetalert2';

const { path } = useRoute();
const jobId = useRoute().params.id;
const isLoading = ref(false);
const JobApplicationData = reactive({
    FirstName: '',
    LastName: '',
    Email: '',
    Phone: '',
    Location: '',
    DOB: '',
    ExperienceYear: '',
    ExperienceDetails: '',
    Qualifications: '',
    WorkType: '',
    DBS: ''
});

const file = ref({});

const message = reactive({
    to: ['admin@early-years.com'],
    subject: '',
    content: ''
})

const { data: job } = await useAsyncData(`content-${path}`, () => 
    queryContent('/jobs').where({ _path: path }).findOne()
);

function resetData() {
    JobApplicationData.FirstName = '',
    JobApplicationData.LastName = '',
    JobApplicationData.Email = '',
    JobApplicationData.Phone = '',
    JobApplicationData.Location = '',
    JobApplicationData.DOB = '',
    JobApplicationData.ExperienceYear = '',
    JobApplicationData.ExperienceDetails = '',
    JobApplicationData.Qualifications = '',
    JobApplicationData.WorkType = '',
    JobApplicationData.DBS = ''
}

const isFormValid = computed(() => {
    return (
        isLoading.value ||
        !JobApplicationData.FirstName ||
        !JobApplicationData.LastName ||
        !JobApplicationData.Email ||
        !JobApplicationData.Phone
    )
})


async function submitForm() {
    isLoading.value = true;
    message.subject = `New job application: ${job.value.Title} - ${job.value.Company} - ${jobId.toUpperCase()} - ${JobApplicationData.FirstName} ${JobApplicationData.LastName}`;

    message.content = "<h2>You have a new job application</h2>" +
        "<h3>Job Title - <span style='color:#64B5D0'>" + job.value.Title + "</span><br>" +
        "Job Location - <span style='color:#64B5D0'>" + job.value.Company + "</span><br>" +
        "Job ID - <span style='color:#64B5D0'>" + jobId.toUpperCase() + "</span></h3>" +
        "<strong>Name: </strong>" + JobApplicationData.FirstName + ' ' + JobApplicationData.LastName + "<br />" +
        "<strong>Email: </strong>" + JobApplicationData.Email + "<br />" +
        "<strong>Phone: </strong>" + JobApplicationData.Phone + "<br />";
        if (JobApplicationData.Location)
            message.content += "<strong>Location: </strong>" + JobApplicationData.Location + "<br />";

        if (JobApplicationData.DOB)
            message.content += "<strong>Date of birth: </strong>" + dayjs(JobApplicationData.DOB).format('DD/MM/YYYY') + "<br />";

        if (JobApplicationData.ExperienceYear)
            message.content += "<strong>How many years of childcare experience do you have?: </strong>" + JobApplicationData.ExperienceYear + "<br />";

        if (JobApplicationData.ExperienceDetails)
            message.content += "<strong>Brief description of your experience in child care: </strong>" + JobApplicationData.ExperienceDetails + "<br />";

        if (JobApplicationData.Qualifications)
            message.content += "<strong>Qualifications: </strong>" + JobApplicationData.Qualifications + "<br />";

        if (JobApplicationData.WorkType)
            message.content += "<strong>What sort of work are you looking for?: </strong>" + JobApplicationData.WorkType + "<br />";

        if (JobApplicationData.DBS)
            message.content += "<strong>Do you have a current DBS?: </strong>" + JobApplicationData.DBS + "<br />";

        try {
            const { data: result } = await $fetch('/api/sendJobApplication', {
                method: 'POST',
                body: message
            });
            if(result) {
                console.log(result)
                await Swal.fire({
                    icon: 'success',
                    title: 'Thank you for applying the job',
                    text: 'Our recruitment team will get back to you in 48 hours.',
                    confirmButtonColor: '#313131',
                });
                isLoading.value = false;
                resetData();
            }
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'An Error Occurred',
                text: 'Some error has occurred',
                confirmButtonColor: '#313131',
            });
            isLoading.value = false;
            resetData();
        }

};
useHead({
    title: `${job.value.Title} at ${job.value.Company}`
});
</script>

<style lang="scss" scoped>
.content {
    :deep() {
        h4 {
            font-size: 35px;
            margin-bottom: 24px;
            font-weight: 500;
            color: $brand-primary;
        }
        p {
            margin-bottom: 24px;
        }
        ul {
            margin-bottom: 24px;
            li {
                line-height: 1.3;
                position: relative;
                padding-left: 24px;
                margin-bottom: 12px;
                &:before {
                    position: absolute;
                    content: '';
                    top: 6px;
                    left: 0;
                    margin-right: 12px;
                    border: 1px solid $brand-primary;
                    height: 8px;
                    width: 8px;
                    border-radius: 5px;
                }
            }
        }
    }
}
:deep() {
    .dp__input {
        background-color: $white;
        height: 48px;
        border-radius: 3px;
        border: none;
        &::placeholder {
            color: #313131;
            opacity: 1;
        }
    }
    .dp__action_button {
        font-size: 13px;
    }
}
</style>